import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { confirmDialog } from "primereact/confirmdialog";
import { InputNumberChangeEvent } from "primereact/inputnumber";
import { Tooltip } from "primereact/tooltip";

import NoDataCreated from "../../../Components/NoDataCreated";
import PageLoader from "../../../Components/PageLoader";
import ErrorPage from "../../../Components/ErrorPage";
import { goToTop } from "../../../Components/goToTop";
import { baseURL } from "../../../Api/ApiConstant";
import NumberInput from "../../../Components/NumberInput";
import { getAppCurrencyType } from "../../../Utils/Common";

import { MaterialPojectType } from "../../../Controllers/Types/HeyTraderTypes/Materials/MaterialPojectType";
import { MaterialType } from "../../../Controllers/Types/HeyTraderTypes/Materials/MaterialType";
import { MaterialCategoryType } from "../../../Controllers/Types/HeyTraderTypes/Materials/MaterialCategoryType";
import { MaterialSubCategoryType } from "../../../Controllers/Types/HeyTraderTypes/Materials/MaterialSubCategoryType";
import { useAppDispatch, useAppSelector } from "../../../Controllers/hooks";
import {
  getAllMaterialCategoryList,
  getAllMaterialProjectList,
  getAllMaterialUnitList,
} from "../../../Controllers/Thunks/HeyTraderThunks";
import {
  addMaterial,
  addMaterialCategory,
  addMaterialSubCategory,
  deleteMaterialCategory,
  deleteMaterialData,
  deleteMaterialSubCategory,
  shareMaterials,
  updateMaterial,
  updateMaterialCategory,
  updateMaterialSubCategory,
} from "../../../Api/HeyTraderService/MaterialsService";
import {
  checkModelSelected,
  checkNumber,
  checkText,
} from "../../../Utils/Validator";
import { useSelector } from "react-redux";
import { RootState } from "../../../Controllers/Store";
import { hasPermission } from "../../../Controllers/Selectors/permissionsSelector";
import ShareModal from "../../../Components/ShareModal";
import { mapShareContactType, ShareContactType } from "../../../Controllers/Types/Share/ShareContactType";
import { ShareContactTypes } from "../../../Controllers/Types/HeyTraderTypes/ShareContactTypes";
import { getLoginUserRoleName, UserRoleName } from "../../../Utils/LoginUserRoleName";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";

const Materials: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const toast = useRef<Toast>(null);
  const noImageIcon = "/assets/images/icons/no_img.svg";
  const currencySymbol = getAppCurrencyType();

  const dataLoading = useAppSelector((state) => state?.materialProject?.loader);
  const error = useAppSelector((state) => state?.materialProject?.errorDisplay);
  const ProjectsList = useAppSelector((state) => state.materialProject.materialProjectList);
  const categoryList = useAppSelector((state) => state.materialCategory.materialCategoryList);

  const [loading, setLoading] = useState<boolean>(false);
  const [customCreateLoading, setCustomCreateLoading] = useState<boolean>(false);
  const [dataState, setDataState] = useState<"Add" | "Edit">("Add");
  const [customDataState, setCustomDataState] = useState<"Add" | "Edit">("Add");
  const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);
  const [materialUpdateHasChanges, setMaterialUpdateHasChanges] = useState<boolean>(false);
  const [deleteMaterialLoadingId, setDeleteMaterialLoadingId] = useState<number | null>(null);

  const [projectDetails, setProjectDetails] = useState<MaterialPojectType | null>(null);
  const [materialsData, setMaterialsData] = useState<MaterialType[]>([]);
  const [selectedMaterialData, setSelectedMaterialData] = useState<MaterialType | null>(null);
  const [showMaterialModal, setShowMaterialModal] = useState<boolean>(false);
  const [totalValue, setTotalValue] = useState<number>(0);

  /* Material */
  const [selectedCategory, setSelectedCategory] = useState<MaterialCategoryType | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<MaterialSubCategoryType | null>(null);
  const [length, setLength] = useState<number | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [height, setHeight] = useState<number | null>(null);

  const [categoryError, setCategoryError] = useState<string>("");
  const [subCategoryError, setSubCategoryError] = useState<string>("");
  const [lengthError, setLengthError] = useState<string>("");
  const [widthError, setWidthError] = useState<string>("");
  const [heightError, setHeightError] = useState<string>("");

  /* Material category */
  const [showCategoryModal, setShowCategoryModal] = useState<boolean>(false);
  const [categoryData, setCategoryData] = useState<MaterialCategoryType[]>([]);
  const [filteredCategoryData, setFilteredCategoryData] = useState<MaterialCategoryType[]>([]);
  const [categorySearchKey, setCategorySearchKey] = useState<string>("");
  const [customCategoryName, setCustomCategoryName] = useState<string>("");
  const [customCategoryNameError, setCustomCategoryNameError] = useState<string>("");
  const [categoryFor, setCategoryFor] = useState<"Area" | "Volume" | "Non">("Area");
  const [categoryFilterTab, setCategoryFilterTab] = useState<'Library' | 'Custom'>('Library');
  const [selectedCustomCategory, setSelectedCustomCategory] = useState<MaterialCategoryType | null>(null);
  const [deleteCatergoryLoadingId, setDeleteCatergoryLoadingId] = useState<number | null>(null);
  const [categoryUpdateHasChanges, setCategoryUpdateHasChanges] = useState<boolean>(false);

  /* Material sub category */
  const [showSubCategoryModal, setShowSubCategoryModal] = useState<boolean>(false);
  const [subCategoryData, setSubCategoryData] = useState<MaterialSubCategoryType[]>([]);
  const [filteredSubCategoryData, setFilteredSubCategoryData] = useState<MaterialSubCategoryType[]>([]);
  const [subCategorySearchKey, setSubCategorySearchKey] = useState<string>("");
  const [customSubCategoryName, setCustomSubCategoryName] = useState<string>("");
  const [customSubCategoryNameError, setCustomSubCategoryNameError] = useState<string>("");
  const [selectedCustomSubCategory, setSelectedCustomSubCategory] = useState<MaterialSubCategoryType | null>(null);
  const [deleteSubCatergoryLoadingId, setDeleteSubCatergoryLoadingId] = useState<number | null>(null);
  const [subCategoryUpdateHasChanges, setSubCategoryUpdateHasChanges] = useState<boolean>(false);


  // share features 
  const [showContactShareModal, setShowContactShareModal] = useState<boolean>(false);
  const [sharedList, setSharedList] = useState<ShareContactType[]>([]);
  const [projectContactIds, setProjectContactIds] = useState<number[]>([]);
  const [materialContactsData, setMaterialContactsData] = useState<ShareContactTypes[]>([]);

    // CHANGE 1
  const [loginUserRoleName, setLoginUserRoleName] = useState<UserRoleName | ''>('');

    // CHANGE 3
  useEffect(() => {
    const fetchUserRole = async () => {
      const roleName = await getLoginUserRoleName();
      setLoginUserRoleName(roleName);
    };

    fetchUserRole();
  }, []);

  useEffect(() => {
    materialDataGet();
    dispatch(getAllMaterialCategoryList());
  }, [dispatch]);

  const materialDataGet = async () => {
    await dispatch(getAllMaterialProjectList());
    setInitialDataLoading(false);
  }


  // PERMISION HANDLER
  const useCanEdit = (screen: string, permission: string) => {
    return useSelector((state: RootState) =>
      hasPermission(state, screen, permission)
    );
  };
 // CHANGE 2
  // const canEditGbl = location.state.projectData ? (location?.state?.projectData?.can_edit === true) : (projectDetails && projectDetails.can_edit === true);


  const hasPermissionForAddMaterial = ((useCanEdit("Materials", "add_item") && projectDetails?.is_own_project) || projectDetails?.can_edit);
  const hasPermissionForEditMaterial = ((useCanEdit("Materials", "edit_item") && projectDetails?.is_own_project) || projectDetails?.can_edit);
  const hasPermissionForDeleteMaterial = ((useCanEdit("Materials", "delete_item") && projectDetails?.is_own_project) || projectDetails?.can_edit);

  // //   chnage reail time responce with updated project info/
  // useEffect(() => {
  //   if (location.state && location.state.projectData) {
  //     const globleproject = location.state.projectData as MaterialPojectType;
  //     const listpro = ProjectsList.filter(
  //       (e) => e.project_id === globleproject.project_id
  //     );
  //     if (listpro.length > 0) {
  //       setProjectDetails(listpro[0] as MaterialPojectType);
  //     } else {
  //       setProjectDetails(globleproject);
  //     }
  //   }
  // }, [location.state, ProjectsList]);


 // CHANGE-4
  // find project from globle object list.
  useEffect(() => {
    if (loginUserRoleName === "commercial client" || loginUserRoleName === "domestic client") {
      if (ProjectsList && ProjectsList?.length > 0) {
        setProjectDetails(ProjectsList[0]);
        // setOwanProject(ProjectsList[0]?.is_own_project);
      } else {
        navigate('/materials/projects')
      }
    } else {

      if (location.state.projectData) {
        const globleproject = location.state.projectData as MaterialPojectType;
        // get login user type here
        // setOwanProject(globleproject?.is_own_project);
        const projectfromState = ProjectsList.filter(
          (e) => e.project_id === globleproject.project_id
        );
        if (projectfromState.length > 0) {
          setProjectDetails(projectfromState[0]);
        } else {
          setProjectDetails(globleproject);
        }
      }
    }


  }, [ProjectsList, location.state]);



  // project contact ids set
  useEffect(() => {
    setMaterialContactsData(projectDetails?.contact || []);
  }, [projectDetails])

  // refill shared list data
  useEffect(() => {
    if (!projectDetails) return;

    const shareList = projectDetails.share_list ?? [];
    if (shareList.length > 0) {
      const results: ShareContactType[] = shareList.map((e) => {
        const dt: Partial<ShareContactType> = {
          id: e.id,
          first_name: e.first_name,
          last_name: e.last_name!,
          profile_image: e.profile_image,
          email: e.email!,
          type: e.main_type!,
          by: e.by,
          can_edit: e.can_edit,
          can_view: 1, // forcing as "viewable"
        };

        return mapShareContactType(dt);
      });

      setSharedList(results);
    } else {
      setSharedList([]); // clear if no share list
    }
  }, [projectDetails]);

  useEffect(() => {
    if (materialContactsData && materialContactsData.length > 0) {
      const ids = materialContactsData.map((e) => e.id)
      setProjectContactIds(ids);
    }
  }, [materialContactsData])

  /* ******************************* Material ********************************** */
  useEffect(() => {
    const fetchData = async () => {
      if (projectDetails && projectDetails !== null) {
        setMaterialsData(projectDetails?.material_data || []);
      } else {
        setMaterialsData([]);
      }
    };
    fetchData();
  }, [projectDetails]);

  useEffect(() => {
    if (materialsData && materialsData?.length > 0) {
      const totalSpend = materialsData.reduce((sum, e) => sum + e.total_spend!, 0);
      setTotalValue(totalSpend);
    } else {
      setTotalValue(0);
    }
  }, [materialsData])




  // new share options
  // top area

  /* Share  */
  const handleCloseShareModal = () => {
    setShowContactShareModal(false);
  }

  const handleShareMaterial = () => {
    setShowContactShareModal(true);
    // setSelectedProject(project);
  }

  // database function
  const shareDataFunction = async (selectedContacts: ShareContactType[]) => {
    // get only ticked data as well
    // const sendData = selectedContacts.filter((e) => e.can_view !== 0 || e.can_edit !== 0)
    const sendData = selectedContacts.filter((e) => e.can_view !== 0)
    if ((sendData && sendData?.length > 0) || true) {
      setLoading(true);
      const data = await shareMaterials(
        sendData,
        projectDetails?.project_id!

      );
      setLoading(false);

      if (data.statusCode === 200) {
        materialDataGet();
        handleCloseShareModal();

        if (toast.current) {
          toast.current.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Material shared successfully.',
            life: 3000
          });
        }
      } else {
        if (toast.current) {
          toast.current.show({
            severity: 'error',
            summary: 'Failed',
            detail: 'There was an error sharing the material.',
            life: 3000
          });
        }
      }
    }
  }
  /*  */
  // end

  const handleCloseMaterialModal = () => {
    setShowMaterialModal(false);
    handleClearMaterialFields();
    handleClearMaterialErrors();
  };

  const handleClearMaterialFields = () => {
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setLength(null);
    setWidth(null);
    setHeight(null);
  };

  const handleClearMaterialErrors = () => {
    setCategoryError("");
    setSubCategoryError("");
    setLengthError("");
    setWidthError("");
    setHeightError("");
  };

  const handleAddMaterial = () => {
    setShowMaterialModal(true);
    setDataState("Add");
  };

  // validations
  useEffect(() => {
    checkModelSelected(selectedCategory != null, false, setCategoryError);
  }, [selectedCategory]);

  useEffect(() => {
    checkModelSelected(selectedSubCategory != null, false, setSubCategoryError);
  }, [selectedSubCategory]);

  useEffect(() => {
    checkNumber(length!, false, setLengthError);
  }, [length]);

  useEffect(() => {
    checkNumber(width!, false, setWidthError);
  }, [width]);

  useEffect(() => {
    checkNumber(height!, false, setHeightError);
  }, [height]);

  function checkCredentials(): boolean {
    const categoryCheck = checkModelSelected(selectedCategory != null, true, setCategoryError);
    const subCategoryCheck = checkModelSelected(selectedSubCategory != null, true, setSubCategoryError);

    if (selectedCategory && selectedCategory.for === "Area") {
      const lenthCheck = checkNumber(length!, true, setLengthError);
      const widthCheck = checkNumber(width!, true, setWidthError);
      return categoryCheck && subCategoryCheck && lenthCheck && widthCheck;

    } else if (selectedCategory && selectedCategory.for === "Volume") {
      const lenthCheck = checkNumber(length!, true, setLengthError);
      const widthCheck = checkNumber(width!, true, setWidthError);
      const heightCheck = checkNumber(height!, true, setHeightError);
      return categoryCheck && subCategoryCheck && lenthCheck && widthCheck && heightCheck;
    } else {

      return categoryCheck && subCategoryCheck;
    }
  }

  function showAPIErrorMaterial(keyName: string, error: string): void {
    if (keyName === "sub_category_id") {
      setSubCategoryError(error);
    }

    if (keyName === "category_id") {
      setCategoryError(error);
    }
  }

  // material create options
  const handleCreateMaterial = async (e: React.FormEvent) => {
    e.preventDefault();

    if (checkCredentials()) {
      setLoading(true);
      const data = await addMaterial(
        projectDetails?.project_id!,
        selectedCategory?.id!,
        selectedSubCategory?.id!,
        length!,
        width!,
        height!
      );

      setLoading(false);

      if (data.statusCode === 200) {
        materialDataGet();
        handleCloseMaterialModal();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Material created successfully.",
            life: 3000,
          });
        }
      } else if (data.statusCode === 422) {
        Object.entries(data.errors ?? []).map(([key, value], index) => (
          showAPIErrorMaterial(key, value[0])
        ));
      } else {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error creating the material.",
            life: 3000,
          });
        }
      }
    }
  };

  const handleEditMaterial = (data: MaterialType) => {
    const materialList = data;
    const categoryList: MaterialCategoryType = materialList?.material_category!;
    const subCategoryList: MaterialSubCategoryType = materialList?.material_sub_category!;

    if (materialList) {
      setShowMaterialModal(true);
      setDataState("Edit");
      setSelectedMaterialData(materialList);

      if (categoryList) {
        const activeSubCategoryLists =
          categoryList?.material_sub_category?.filter(
            (subCategory) => subCategory?.status === 1
          );
        if (activeSubCategoryLists && activeSubCategoryLists.length > 0) {
          setSubCategoryData(activeSubCategoryLists);
        } else {
          setSubCategoryData([]);
        }
        setSelectedCategory(categoryList!);
      } else {
        setSelectedCategory(null);
      }

      if (subCategoryList) {
        setSelectedSubCategory(subCategoryList!);
      } else {
        setSelectedSubCategory(null);
      }

      setLength(materialList?.length || null);
      setWidth(materialList?.width || null);
      setHeight(materialList?.hight || null);
    } else {
      setSelectedMaterialData(null);
    }
  };

  useEffect(() => {
    if (selectedMaterialData) {
      if (selectedCategory?.id !== selectedMaterialData?.category_id) {
        setMaterialUpdateHasChanges(true);
      } else if (selectedSubCategory?.id !== selectedMaterialData?.sub_category_id) {
        setMaterialUpdateHasChanges(true);
      } else if (length !== null && length !== selectedMaterialData?.length) {
        setMaterialUpdateHasChanges(true);
      } else if (width !== null && width !== selectedMaterialData?.width) {
        setMaterialUpdateHasChanges(true);
      } else if (height !== null && height !== selectedMaterialData?.hight) {
        setMaterialUpdateHasChanges(true);
      } else {
        setMaterialUpdateHasChanges(false);
      }
    }
  }, [selectedMaterialData, selectedCategory, selectedSubCategory, length, width, height]);

  // update materials data
  const handleUpdateMaterial = async (e: React.FormEvent) => {
    e.preventDefault();

    if (checkCredentials()) {
      setLoading(true);

      const data = await updateMaterial(
        projectDetails?.project_id!,
        selectedCategory?.id!,
        selectedSubCategory?.id!,
        length!,
        width!,
        height!,
        selectedMaterialData?.id!
      );

      setLoading(false);

      if (data.statusCode === 200) {
        materialDataGet();
        handleCloseMaterialModal();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Material updated successfully.",
            life: 3000,
          });
        }
      } else if (data.statusCode === 422) {
        Object.entries(data.errors ?? []).map(([key, value], index) => (
          showAPIErrorMaterial(key, value[0])
        ));
      } else {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error updating the material.",
            life: 3000,
          });
        }
      }
    }
  };

  const handleDeleteMaterial = (data: MaterialType) => {
    const materialId = data?.id;

    confirmDialog({
      message: "Are you sure you want to delete this material?",
      header: "Confirm the deletion",
      icon: "bi bi-info-circle",
      defaultFocus: "accept",
      acceptClassName: "p-button-danger",
      accept: () => deleteMaterial(materialId!),
      dismissableMask: true,
      resizable: false,
      draggable: false,
    });
  };

  const deleteMaterial = async (materialId: number) => {
    setDeleteMaterialLoadingId(materialId);
    const result = await deleteMaterialData(materialId);

    setDeleteMaterialLoadingId(null);
    if (result.statusCode === 200) {
      materialDataGet();

      if (toast.current) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Material deleted successfully.",
          life: 3000,
        });
      }
    } else {
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Failed",
          detail: "There was an error deleting the material.",
          life: 3000,
        });
      }
    }
  };

  const materialModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">{dataState} material</h5>

      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={handleCloseMaterialModal}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  const materialModalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Close"
        className="custom_btn secondary"
        onClick={handleCloseMaterialModal}
      />

      <Button
        label={`${loading ? "Processing" : dataState === "Add" ? "Save" : "Update"}`}
        onClick={dataState === "Add" ? handleCreateMaterial : handleUpdateMaterial}
        loading={loading}
        className="custom_btn primary"
        disabled={dataState === 'Edit' && !materialUpdateHasChanges}
      />
    </div>
  );

  const handleLengthChange = (e: InputNumberChangeEvent) => {
    if (e.value != null) {
      setLength(e.value);
    }
  };

  const handleWidthChange = (e: InputNumberChangeEvent) => {
    if (e.value != null) {
      setWidth(e.value);
    }
  };

  const handleHeightChange = (e: InputNumberChangeEvent) => {
    if (e.value != null) {
      setHeight(e.value);
    }
  };

  const handleNavigateMaterialItem = (data: MaterialType) => {
    const materialList = data;
    navigate(`items`, { state: { materialList: materialList } });
  };

  /* For table view */
  const materialImageBody = (rowData: MaterialType) => {
    const materialCategoryData: MaterialCategoryType = rowData?.material_category!;
    const materialCategoryImage = materialCategoryData?.path && baseURL + materialCategoryData?.path;
    const materialCategoryName = materialCategoryData?.category_name ? materialCategoryData?.category_name : "";
    const materialCategoryFor = materialCategoryData?.for ? materialCategoryData?.for : "";

    return (
      <div className="tool_display">
        {materialCategoryImage ? (
          <img
            src={materialCategoryImage}
            className="tool_display_img"
            alt={materialCategoryName}
          />
        ) : (
          <span className="tool_display_img no_img">
            <img src={noImageIcon!} alt={materialCategoryName} />
          </span>
        )}

        <span>
          {materialCategoryName} <br />
          <small className="text_sub">({materialCategoryFor === 'Non' ? 'None' : materialCategoryFor})</small>
        </span>
      </div>
    );
  };

  const tableActionBody = (rowData: MaterialType) => {
    const data = rowData;
    return (
      <React.Fragment>
        <Tooltip
          key={`item_${data?.id}`}
          id={`item_${data?.id}`}
          target={`.item_${data?.id}`}
        />

        {hasPermissionForEditMaterial && (
          <Tooltip
            key={`edit_${data?.id}`}
            id={`edit_${data?.id}`}
            target={`.edit_${data?.id}`}
          />
        )}

        {hasPermissionForDeleteMaterial && (
          <Tooltip
            key={`delete_${data?.id}`}
            id={`delete_${data?.id}`}
            target={`.delete_${data?.id}`}
          />
        )}

        <div className="tabel_btn_area custom mt-1 mb-1">
          <button
            className={`data_action_btn with_bg primary_table_btn item_${data?.id} p-ripple`}
            onClick={() => handleNavigateMaterialItem(data)}
            data-pr-tooltip={`Material items`}
            data-pr-position="top"
          >
            <i className="bi bi-boxes"></i>
            <Ripple />
          </button>

          {hasPermissionForEditMaterial && (
            <button
              className={`data_action_btn with_bg primary_table_btn edit_${data?.id} p-ripple`}
              onClick={() => handleEditMaterial(data)}
              data-pr-tooltip={`Edit material`}
              data-pr-position="top"
            >
              <i className="bi bi-pencil-square"></i>
              <Ripple />
            </button>
          )}

          {hasPermissionForDeleteMaterial && (
            <button
              className={`data_action_btn with_bg danger_table_btn delete_${data?.id} p-ripple`}
              onClick={() => handleDeleteMaterial(data)}
              data-pr-tooltip={`Delete material`}
              data-pr-position="top"
              disabled={deleteMaterialLoadingId === data?.id}
            >
              <i className={`${deleteMaterialLoadingId === data?.id ? 'bi bi-arrow-repeat loading_spinner' : 'bi bi-trash3'}`}></i>
              <Ripple />
            </button>
          )}
        </div>
      </React.Fragment>
    );
  };
  /* *************************************************************************** */

  /* *************************** Material category ***************************** */
  // load data from api responce
  useEffect(() => {
    const fetchData = async () => {
      const activeCategoryLists = categoryList.filter((category) => category?.status === 1);

      if (activeCategoryLists && activeCategoryLists.length > 0) {
        setCategoryData(activeCategoryLists);

        if (selectedCategory != null) {
          const listfind = activeCategoryLists.filter(
            (e) => e.id === selectedCategory.id
          );
          if (listfind.length > 0) {
            // reset selected category
            setSelectedCategory(listfind[0]);
            // set subcategory list
            const activeSubCategoryLists =
              listfind[0]?.material_sub_category?.filter(
                (subCategory) => subCategory?.status === 1
              );
            if (activeSubCategoryLists && activeSubCategoryLists.length > 0) {
              setSubCategoryData(activeSubCategoryLists);
            } else {
              setSubCategoryData([]);
            }
          }
        }
      } else {
        setCategoryData([]);
      }
    };
    fetchData();
  }, [categoryList]);

  // category filter actions
  useEffect(() => {
    if (categoryData && categoryData?.length > 0) {
      let filteredData: MaterialCategoryType[] = [];

      if (categoryFilterTab === 'Library') {
        filteredData = categoryData?.filter(data => data?.created_by === null);
      } else if (categoryFilterTab === 'Custom') {
        filteredData = categoryData?.filter(data => data?.created_by !== null);
      } else {
        filteredData = categoryData;
      }

      const filteredCategories = filteredData.filter((category) =>
        category
          ?.category_name!.toLowerCase()
          .includes(categorySearchKey.toLowerCase())
      );
      setFilteredCategoryData(filteredCategories);
    } else {
      setFilteredCategoryData([]);
    }
  }, [categoryData, categorySearchKey, categoryFilterTab]);

  const handleCloseCategoryModal = () => {
    setShowCategoryModal(false);
    setShowMaterialModal(true);
    setCategorySearchKey("");

    handleClearCustomCategoryFields();
  };

  const handleClearCustomCategoryFields = () => {
    setCustomCategoryName("");
    setCategoryFor("Area");
    setCustomCategoryNameError("");
    setSelectedCustomCategory(null);
    setCustomDataState('Add');
  };

  const handleAddCategory = () => {
    setShowCategoryModal(true);
    setShowMaterialModal(false);
    setCustomDataState('Add');
  };

  const handleSelectCategory = (category: MaterialCategoryType) => {
    setSelectedCategory(category);
    setSelectedSubCategory(null);

    if (category) {
      const activeSubCategoryLists = category?.material_sub_category?.filter(
        (subCategory) => subCategory?.status === 1
      );
      if (activeSubCategoryLists && activeSubCategoryLists.length > 0) {
        setSubCategoryData(activeSubCategoryLists);
      } else {
        setSubCategoryData([]);
      }
    } else {
      setSubCategoryData([]);
    }

    setTimeout(() => {
      handleCloseCategoryModal();
    }, 200);
  };

  function showAPIErrorCategory(keyName: string, error: string): void {
    if (keyName === "category_name") {
      setCustomCategoryNameError(error);
    }
  }

  useEffect(() => {
    checkText(customCategoryName, false, setCustomCategoryNameError);
  }, [customCategoryName])

  function checkCredentialsForCategory(): boolean {
    const customCategoryCheck = checkText(customCategoryName, true, setCustomCategoryNameError);

    return customCategoryCheck;
  }

  // create custom category.
  const handleCreateCustomCategory = async (e: React.FormEvent) => {
    e.preventDefault();

    if (checkCredentialsForCategory()) {
      setCustomCreateLoading(true);
      const data = await addMaterialCategory(customCategoryName, categoryFor);

      if (data.statusCode === 200) {
        // call dispach event
        dispatch(getAllMaterialCategoryList());

        setCustomCreateLoading(false);
        handleClearCustomCategoryFields();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Category created successfully.",
            life: 3000,
          });
        }
      } else if (data.statusCode === 422) {
        setCustomCreateLoading(false);
        Object.entries(data.errors ?? []).map(([key, value], index) => (
          showAPIErrorCategory(key, value[0])
        ));
      } else {
        setCustomCreateLoading(false);
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error creating the category.",
            life: 3000,
          });
        }
      }
    }
  };

  const handleEditCustomCategory = (category: MaterialCategoryType) => {
    if (category) {
      setSelectedCustomCategory(category);

      setCustomCategoryName(category?.category_name || '');
      setCategoryFor(category?.for);
      setCustomDataState('Edit');
    } else {
      setSelectedCustomCategory(null);
    }
  }

  useEffect(() => {
    if (selectedCustomCategory) {
      if (customCategoryName && customCategoryName !== selectedCustomCategory?.category_name) {
        setCategoryUpdateHasChanges(true);
      } else {
        setCategoryUpdateHasChanges(false);
      }
    }
  }, [selectedCustomCategory, customCategoryName]);

  // update custom category.
  const handleUpdateCustomCategory = async (e: React.FormEvent) => {
    e.preventDefault();

    if (checkCredentialsForCategory()) {
      setCustomCreateLoading(true);

      const data = await updateMaterialCategory(
        customCategoryName,
        categoryFor,
        selectedCustomCategory?.id!,
      );

      if (data.statusCode === 200) {
        // call dispach event
        dispatch(getAllMaterialCategoryList());

        setCustomCreateLoading(false);
        handleClearCustomCategoryFields();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Category updated successfully.",
            life: 3000,
          });
        }
      } else if (data.statusCode === 422) {
        setCustomCreateLoading(false);
        Object.entries(data.errors ?? []).map(([key, value], index) => (
          showAPIErrorCategory(key, value[0])
        ));
      } else {
        setCustomCreateLoading(false);
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error updating the category.",
            life: 3000,
          });
        }
      }
    }
  };

  const handleDeleteCustomCategory = (category: MaterialCategoryType) => {
    if (category) {
      handleClearCustomCategoryFields();
      const customCategoryId = category?.id;

      confirmDialog({
        message: "Are you sure you want to delete this category?",
        header: "Confirm the deletion",
        icon: "bi bi-info-circle",
        defaultFocus: "accept",
        acceptClassName: "p-button-danger",
        accept: () => deleteCustomCategory(customCategoryId!),
        dismissableMask: true,
        resizable: false,
        draggable: false,
      });
    }
  }

  const deleteCustomCategory = async (customCategoryId: number) => {
    if (customCategoryId) {
      setDeleteCatergoryLoadingId(customCategoryId);
      const result = await deleteMaterialCategory(customCategoryId);

      setDeleteCatergoryLoadingId(null);
      if (result.statusCode === 200) {
        // call dispach event
        dispatch(getAllMaterialCategoryList());
        setSelectedCategory(null);

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Category deleted successfully.",
            life: 3000,
          });
        }
      } else {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error deleting the category.",
            life: 3000,
          });
        }
      }
    }
  }

  const handleChangeCategoryFor = (
    e: CheckboxChangeEvent,
    option: "Area" | "Volume" | "Non"
  ) => {
    setCategoryFor(option);
  };


  const categoryModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">
        {selectedCategory === null ? 'Select' : 'Change'} category
      </h5>

      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={handleCloseCategoryModal}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  const categoryModalFooter = (
    <div className="page_form_group data_select_modal_footer mb-0 in_modal_footer">
      <div className="page_form_group mt-0 mb-3">
        <div className="response_state_area pt-0">
          <div className="response_state">
            <Checkbox
              inputId="area_option"
              name="area"
              value={categoryFor}
              className="checkbox_success"
              onChange={(e) => handleChangeCategoryFor(e, "Area")}
              checked={categoryFor === "Area"}
            />
            <label htmlFor="area_option">Area</label>
          </div>
          <div className="response_state">
            <Checkbox
              inputId="volume_option"
              name="volume"
              value={categoryFor}
              className="checkbox_success"
              onChange={(e) => handleChangeCategoryFor(e, "Volume")}
              checked={categoryFor === "Volume"}
            />
            <label htmlFor="volume_option">Volume</label>
          </div>

          <div className="response_state">
            <Checkbox
              inputId="non_option"
              name="Non"
              value={categoryFor}
              className="checkbox_success"
              onChange={(e) => handleChangeCategoryFor(e, "Non")}
              checked={categoryFor === "Non"}
            />
            <label htmlFor="non_option">None</label>
          </div>
        </div>
      </div>
      <div className="input_with_button_area">
        <InputText
          id="customCategoryName"
          value={customCategoryName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomCategoryName(e.target.value)}
          className="custom_form_input has_add_value custom_data_input"
          placeholder="Enter category name"
          autoComplete="off"
        />
        <button
          className="add_value_btn fill p-ripple"
          onClick={customDataState === 'Add' ? handleCreateCustomCategory : handleUpdateCustomCategory}
          disabled={(customCreateLoading || customCategoryName === "") || (customDataState === 'Edit' && !categoryUpdateHasChanges)}
        >
          <i className={customCreateLoading ? "ri-loader-4-fill loading_spinner" : customDataState === 'Edit' ? "bi bi-pencil-square" : "bi bi-plus-lg"}></i>
          <span>{customCreateLoading ? "Processing" : customDataState === 'Edit' ? "Update" : "Add"}</span>
          <Ripple />
        </button>
      </div>
      {customCategoryNameError && (
        <small className="form_error_msg text-start">
          {customCategoryNameError}
        </small>
      )}
    </div>
  )
  /* *************************************************************************** */

  /* ************************* Material sub category *************************** */
  useEffect(() => {
    if (subCategoryData && subCategoryData?.length > 0) {
      const filteredSubCategories = subCategoryData.filter((subCategory) =>
        subCategory
          ?.sub_category_name!.toLowerCase()
          .includes(subCategorySearchKey.toLowerCase())
      );
      setFilteredSubCategoryData(filteredSubCategories);
    } else {
      setFilteredSubCategoryData([]);
    }
  }, [subCategoryData, subCategorySearchKey]);

  const handleCloseSubCategoryModal = () => {
    setShowSubCategoryModal(false);
    setShowMaterialModal(true);
    setSubCategorySearchKey("");

    handleClearCustomSubCategoryFields();
  };

  const handleClearCustomSubCategoryFields = () => {
    setCustomSubCategoryName("");
    setCustomSubCategoryNameError("");
    setCustomDataState('Add');
    setSelectedCustomSubCategory(null);
  };

  const handleAddSubCategory = () => {
    setShowSubCategoryModal(true);
    setShowMaterialModal(false);
    setCustomDataState('Add');
  };

  const handleSelectSubCategory = (subCategory: MaterialSubCategoryType) => {
    setSelectedSubCategory(subCategory);

    setTimeout(() => {
      handleCloseSubCategoryModal();
    }, 200);
  };


  function showAPIErrorSubCategory(keyName: string, error: string): void {
    if (keyName === "sub_category_name") {
      setCustomSubCategoryNameError(error);
    }
  }

  useEffect(() => {
    checkText(customSubCategoryName, false, setCustomSubCategoryNameError);
  }, [customSubCategoryName])

  function checkCredentialsForSubCategory(): boolean {
    const customSubCategoryCheck = checkText(customSubCategoryName, true, setCustomSubCategoryNameError);
    const selectedCategoryCheck = selectedCategory !== null;

    return customSubCategoryCheck && selectedCategoryCheck;
  }

  const handleCreateCustomSubCategory = async (e: React.FormEvent) => {
    e.preventDefault();

    if (checkCredentialsForSubCategory()) {
      setCustomCreateLoading(true);
      const data = await addMaterialSubCategory(
        selectedCategory?.id!,
        customSubCategoryName
      );

      if (data.statusCode === 200) {
        // call dispach event
        dispatch(getAllMaterialCategoryList());

        setCustomCreateLoading(false);
        handleClearCustomSubCategoryFields();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Subcategory created successfully.",
            life: 3000,
          });
        }
      } else if (data.statusCode === 422) {
        setCustomCreateLoading(false);
        Object.entries(data.errors ?? []).map(([key, value], index) => (
          showAPIErrorSubCategory(key, value[0])
        ));
      } else {
        setCustomCreateLoading(false);
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error creating the subcategory.",
            life: 3000,
          });
        }
      }
    }
  };

  const handleEditCustomSubCategory = (subCategory: MaterialSubCategoryType) => {
    if (subCategory) {
      setSelectedCustomSubCategory(subCategory);

      setCustomSubCategoryName(subCategory?.sub_category_name || '');
      setCustomDataState('Edit');
    } else {
      setSelectedCustomSubCategory(null);
    }
  }

  useEffect(() => {
    if (selectedCustomSubCategory) {
      if (customSubCategoryName && customSubCategoryName !== selectedCustomSubCategory?.sub_category_name) {
        setSubCategoryUpdateHasChanges(true);
      } else {
        setSubCategoryUpdateHasChanges(false);
      }
    }
  }, [selectedCustomSubCategory, customSubCategoryName]);

  const handleUpdateCustomSubCategory = async (e: React.FormEvent) => {
    e.preventDefault();

    if (checkCredentialsForSubCategory() && selectedCustomSubCategory !== null) {
      setCustomCreateLoading(true);
      const data = await updateMaterialSubCategory(
        selectedCategory?.id!,
        customSubCategoryName,
        selectedCustomSubCategory?.id
      );

      if (data.statusCode === 200) {
        // call dispach event
        dispatch(getAllMaterialCategoryList());

        setCustomCreateLoading(false);
        handleClearCustomSubCategoryFields();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Sub category updated successfully.",
            life: 3000,
          });
        }
      } else if (data.statusCode === 422) {
        setCustomCreateLoading(false);
        Object.entries(data.errors ?? []).map(([key, value], index) => (
          showAPIErrorSubCategory(key, value[0])
        ));
      } else {
        setCustomCreateLoading(false);
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error updating the sub category.",
            life: 3000,
          });
        }
      }
    }
  };

  const handleDeleteCustomSubCategory = (subCategory: MaterialSubCategoryType) => {
    if (subCategory) {
      handleClearCustomSubCategoryFields();
      const customSubCategoryId = subCategory?.id;

      confirmDialog({
        message: "Are you sure you want to delete this sub category?",
        header: "Confirm the deletion",
        icon: "bi bi-info-circle",
        defaultFocus: "accept",
        acceptClassName: "p-button-danger",
        accept: () => deleteCustomSubCategory(customSubCategoryId!),
        dismissableMask: true,
        resizable: false,
        draggable: false,
      });
    }
  }

  const deleteCustomSubCategory = async (customSubCategoryId: number) => {
    if (customSubCategoryId) {
      setDeleteSubCatergoryLoadingId(customSubCategoryId);
      const result = await deleteMaterialSubCategory(customSubCategoryId);

      setDeleteSubCatergoryLoadingId(null);
      if (result.statusCode === 200) {
        // call dispach event
        dispatch(getAllMaterialCategoryList());
        setSelectedSubCategory(null);

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Sub category deleted successfully.",
            life: 3000,
          });
        }
      } else {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error deleting the sub category.",
            life: 3000,
          });
        }
      }
    }
  }
  /* *************************************************************************** */

  /* **************************** Common functions ***************************** */
  const goBack = () => {
    navigate(-1);
    goToTop();
  };
  /* *************************************************************************** */

  return (
    <React.Fragment>
      <Toast ref={toast} />

      <div className="main_content layout_padding show_side_bar">
        {dataLoading && initialDataLoading ? (
          <PageLoader />
        ) : error ? (
          <ErrorPage errorMessage={error} />
        ) : (
          <div className="page_content mt-0">
            <div className="page_breadcrumb_area">
              <ul className="page_breadcrumb">
                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                  Materials
                </li>

                {/* <li className="page_breadcrumb_item active">
                  {projectDetails?.job_number + ": " + projectDetails?.address}
                </li> */}

                 {/* CHANGE 4 */}
                  {(ProjectsList && ProjectsList?.length === 1) ? (<li className="page_breadcrumb_item has_child">
                    {projectDetails?.job_number + ": " + projectDetails?.address}
                  </li>) : (((loginUserRoleName === "domestic client") || loginUserRoleName === "commercial client")) ? (<li> <Dropdown
                    key={`projectKey`}
                    id="projectKey"
                    value={projectDetails}
                    onChange={(e: DropdownChangeEvent) => setProjectDetails(e.value)}
                    optionLabel="address"
                    options={ProjectsList}
                    panelClassName="status_option_panel"
                    placeholder="Select collection"
                    className="form_dropdown w-100"
                  // loading={loadingFSKitchenCollections}
                  /> </li>) : (<li className="page_breadcrumb_item has_child">
                    {projectDetails?.job_number + ": " + projectDetails?.address}
                  </li>)}



              </ul>

              <button className="back_btn p-ripple" onClick={goBack}>
                <i className="bi bi-arrow-left me-2"></i>
                Back
                <Ripple />
              </button>
            </div>

            <div className="page_header_area custom_flex mt-sm-3 mt-3">
              <div className="display_label_area">
                <div className="display_label">
                  <span className="display_label_text">Total :&nbsp;</span>
                  <span>
                    {currencySymbol}&nbsp;
                    {totalValue.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              <div className="page_action_area">
                {projectDetails?.is_own_project && (
                  <button
                    className="add_data_link_btn custom p-ripple"
                    onClick={handleShareMaterial}
                  >
                    <i className="bi bi-share-fill"></i>
                    <span className="btn_text">Share</span>
                    <Ripple />
                  </button>
                )}

                {hasPermissionForAddMaterial && (
                  <button
                    className="add_data_link_btn p-ripple"
                    onClick={handleAddMaterial}
                  >
                    <i className="bi bi-plus-circle"></i>
                    <span>Add material</span>
                    <Ripple />
                  </button>
                )}
              </div>
            </div>

            <div className="page_main_content_section">
              {materialsData && materialsData?.length > 0 ? (
                <DataTable
                  value={materialsData}
                  paginator
                  size="small"
                  rows={10}
                  emptyMessage="No data found!"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  tableStyle={{ minWidth: "50rem" }}
                  paginatorDropdownAppendTo={"self"}
                  paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                  currentPageReportTemplate="{first} to {last} of {totalRecords}"
                  className="page_table p-0 p-sm-2"
                  rowHover
                  scrollable
                  loading={dataLoading}
                  dataKey="id"
                >
                  <Column
                    header="No."
                    headerClassName="text_purple"
                    className="text_bold"
                    body={(rowData: MaterialType, { rowIndex }) => (
                      <span>{rowIndex + 1}.</span>
                    )}
                    style={{ width: "6%" }}
                  ></Column>

                  <Column
                    header="Category"
                    headerClassName="text_purple"
                    body={materialImageBody}
                    style={{ width: "20%" }}
                  ></Column>

                  <Column
                    header="Subcategory"
                    headerClassName="text_purple"
                    body={(rowData: MaterialType) => (
                      <span className="text_no_wrap">
                        {rowData?.material_sub_category ? rowData?.material_sub_category?.sub_category_name : "---"}
                      </span>
                    )}
                    style={{ width: "20%" }}
                  ></Column>

                  <Column
                    header="Length"
                    alignHeader={"center"}
                    headerClassName="text_purple"
                    body={(rowData: MaterialType) => (
                      <span className="text_no_wrap flex_center">
                        {rowData?.length ? String(rowData?.length).padStart(2, "0") : "---"}
                      </span>
                    )}
                    style={{ width: "8%" }}
                  ></Column>

                  <Column
                    header="Width"
                    alignHeader={"center"}
                    headerClassName="text_purple"
                    body={(rowData: MaterialType) => (
                      <span className="text_no_wrap flex_center">
                        {rowData?.width ? String(rowData?.width).padStart(2, "0") : "---"}
                      </span>
                    )}
                    style={{ width: "8%" }}
                  ></Column>

                  <Column
                    header="Height"
                    alignHeader={"center"}
                    headerClassName="text_purple"
                    body={(rowData: MaterialType) => (
                      <span className="text_no_wrap flex_center">
                        {rowData?.hight !== null ? String(rowData?.hight).padStart(2, "0") : "---"}
                      </span>
                    )}
                    style={{ width: "8%" }}
                  ></Column>

                  <Column
                    header="Amount"
                    alignHeader={"right"}
                    headerClassName="text_purple"
                    field="totalSpend"
                    body={(rowData: MaterialType) => (
                      <span className="text_no_wrap text_bold flex_end">
                        {currencySymbol}&nbsp;
                        {rowData?.total_spend !== null ? rowData?.total_spend?.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00"}
                      </span>
                    )}
                    style={{ width: "15%" }}
                  ></Column>

                  <Column
                    alignHeader="center"
                    body={tableActionBody}
                    frozen={window.innerWidth > 575}
                    alignFrozen="right"
                  ></Column>
                </DataTable>
              ) : (
                <NoDataCreated message={`No materials have been added yet`} />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Material modal */}
      <Dialog
        visible={showMaterialModal}
        headerClassName="custom_modal_header"
        header={materialModalHeader}
        footer={materialModalFooter}
        dismissableMask
        className="custom_modal_dialog modal_dialog_sm"
        onHide={handleCloseMaterialModal}
      >
        <div className="custom_modal_body">
          <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
            <div className="row">
              {/* Category */}
              <div className="col-12">
                <div className="page_form_group">
                  <label
                    htmlFor="category"
                    className="custom_form_label is_required"
                  >
                    Category
                  </label>
                  <div className="input_with_button_area">
                    <InputText
                      key={`category`}
                      id="category"
                      value={selectedCategory && selectedCategory?.category_name}
                      className="custom_form_input has_add_value"
                      placeholder="Click to select the category."
                      readOnly={true}
                      autoComplete="off"
                      onClick={handleAddCategory}
                    />
                    <button
                      className="add_value_btn p-ripple"
                      onClick={handleAddCategory}
                    >
                      {selectedCategory === null ? (
                        <React.Fragment>
                          <i className="bi bi-plus-lg"></i>
                          <span>Add</span>
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <i className="bi bi-pencil-square"></i>
                          <span>Change</span>
                        </React.Fragment>
                      )}
                      <Ripple />
                    </button>
                  </div>

                  {categoryError && (
                    <small className="form_error_msg">{categoryError}</small>
                  )}
                </div>
              </div>

              {/* Subcategory */}
              <div className="col-12">
                <div className="page_form_group">
                  <label
                    htmlFor="subCategory"
                    className="custom_form_label is_required"
                  >
                    Subcategory
                  </label>
                  <div className="input_with_button_area">
                    <InputText
                      key={`subCategory`}
                      id="subCategory"
                      value={selectedSubCategory ? selectedSubCategory?.sub_category_name : ""}
                      className="custom_form_input has_add_value"
                      placeholder="Click to select the subcategory."
                      readOnly={true}
                      autoComplete="off"
                      onClick={handleAddSubCategory}
                      disabled={selectedCategory === null}
                    />
                    <button
                      className="add_value_btn p-ripple"
                      onClick={handleAddSubCategory}
                      disabled={selectedCategory === null}
                    >
                      {selectedSubCategory === null ? (
                        <React.Fragment>
                          <i className="bi bi-plus-lg"></i>
                          <span>Add</span>
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <i className="bi bi-pencil-square"></i>
                          <span>Change</span>
                        </React.Fragment>
                      )}
                      <Ripple />
                    </button>
                  </div>

                  {subCategoryError && (
                    <small className="form_error_msg">{subCategoryError}</small>
                  )}
                </div>
              </div>

              {/* Length */}
              {selectedCategory &&
                (selectedCategory?.for === "Area" ||
                  selectedCategory?.for === "Volume") && (
                  <div className="col-12 col-sm-6">
                    <NumberInput
                      key={`length`}
                      inputId="length"
                      label="Length"
                      labelHtmlFor="length"
                      placeholder="Enter length"
                      onChange={handleLengthChange}
                      value={length}
                      required={true}
                      minFractionDigits={2}
                      maxFractionDigits={2}
                      min={1}
                      error={lengthError}
                      formGroupClassName={selectedCategory && selectedCategory?.for === "Volume" ? "" : " mb-sm-2"}
                    />
                  </div>
                )}

              {/* Width */}
              {selectedCategory &&
                (selectedCategory?.for === "Area" ||
                  selectedCategory?.for === "Volume") && (
                  <div className="col-12 col-sm-6">
                    <NumberInput
                      key={`width`}
                      inputId="width"
                      label="Width"
                      labelHtmlFor="width"
                      placeholder="Enter width"
                      onChange={handleWidthChange}
                      value={width}
                      required={true}
                      minFractionDigits={2}
                      maxFractionDigits={2}
                      min={1}
                      error={widthError}
                      formGroupClassName={selectedCategory && selectedCategory?.for === "Volume" ? "" : " mb-2"}
                    />
                  </div>
                )}

              {/* Height */}
              {selectedCategory && selectedCategory?.for === "Volume" && (
                <div className="col-12 col-sm-6">
                  <NumberInput
                    key={`height`}
                    inputId="height"
                    label="Height"
                    labelHtmlFor="height"
                    placeholder="Enter height"
                    onChange={handleHeightChange}
                    value={height}
                    required={selectedCategory?.for === "Volume"}
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={1}
                    error={heightError}
                    formGroupClassName="mb-2"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Dialog>
      {/*  */}

      {/* Material category modal */}
      <Dialog
        visible={showCategoryModal}
        header={categoryModalHeader}
        footer={categoryFilterTab === 'Custom' ? categoryModalFooter : ''}
        headerClassName="custom_modal_header"
        className="custom_modal_dialog modal_dialog_sm"
        onHide={handleCloseCategoryModal}
        dismissableMask
      >
        <div className="custom_modal_body">
          <div className="data_select_modal_header p-0 pb-3">
            <div className="w-100">
              <div className="add_data_switch_btn_area pb-3">
                <button
                  className={`add_data_switch_btn p-ripple ${categoryFilterTab === "Library" && "active"}`}
                  type="button"
                  role="tab"
                  onClick={() => setCategoryFilterTab("Library")}
                >
                  Library
                  <Ripple />
                </button>

                <button
                  className={`add_data_switch_btn p-ripple ${categoryFilterTab === "Custom" && "active"}`}
                  type="button"
                  role="tab"
                  onClick={() => setCategoryFilterTab("Custom")}
                >
                  Custom
                  <Ripple />
                </button>
              </div>

              <div className="filter_input_grp mb-0">
                <InputText
                  key={`categorySearchKey`}
                  id="categorySearchKey"
                  value={categorySearchKey}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCategorySearchKey(e.target.value)
                  }
                  className="custom_form_input"
                  placeholder="Search category by name"
                  type={"search"}
                />
                {categorySearchKey ? (
                  <i
                    className="bi bi-x-lg data_search_input_clear"
                    onClick={() => setCategorySearchKey("")}
                  ></i>
                ) : (
                  <i className="bi bi-search"></i>
                )}
              </div>
            </div>
          </div>

          <div className="data_select_modal_body">
            {filteredCategoryData && filteredCategoryData?.length > 0 ? (
              <div className="data_select_option_area">
                {filteredCategoryData?.map((category) => (
                  <div
                    key={category?.id}
                    className={`data_select_option has_img p-ripple ${category?.id === selectedCategory?.id ? "selected" : ""}
                    ${categoryFilterTab === 'Custom' ? 'custom' : ''}`}
                    onClick={() => handleSelectCategory(category)}
                  >
                    {category?.path ? (
                      <img
                        src={baseURL + category?.path}
                        className="option_image"
                        alt={category?.category_name || ''}
                      />
                    ) : (
                      <span className="option_image no_img">
                        <img src={noImageIcon} alt={category?.category_name || ''} />
                      </span>
                    )}
                    <span>{category?.category_name}</span>
                    <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />

                    {categoryFilterTab === 'Custom' && (
                      <div className="custom_data_action_area">
                        <button className="is_btn data_select_option_edit_btn p-ripple"
                          onClick={(e) => {
                            e.stopPropagation();

                            if (selectedCustomCategory !== null) {
                              handleClearCustomCategoryFields();
                            } else {
                              handleEditCustomCategory(category);
                            }
                          }}>
                          <i className={`bi ${selectedCustomCategory !== null && selectedCustomCategory?.id === category?.id ? 'bi-x-lg' : 'bi-pencil-square'}`}></i>
                        </button>

                        <button className="is_btn data_select_option_edit_btn delete p-ripple"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCustomCategory(category);
                          }}
                          disabled={deleteCatergoryLoadingId === category?.id}
                        >
                          <i className={deleteCatergoryLoadingId === category?.id ? 'ri-loader-4-fill loading_spinner' : 'bi bi-trash'}></i>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no_records_found">
                <i className="bi bi-exclamation-circle me-1"></i>
                No categories found.
              </div>
            )}
          </div>
        </div>
      </Dialog>
      {/*  */}

      {/* Material sub category modal */}
      <Dialog
        visible={showSubCategoryModal}
        headerClassName="d-none"
        dismissableMask
        className="custom_modal_dialog modal_dialog_sm data_select_modal"
        contentClassName="data_select_modal_content"
        onHide={handleCloseSubCategoryModal}
      >
        <div className="custom_modal_body data_select_modal_area">
          <div className="data_select_modal_header">
            <div className="data_search_input_area">
              <InputText
                key={"subCategorySearchKey"}
                id="subCategorySearchKey"
                value={subCategorySearchKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubCategorySearchKey(e.target.value)}
                className="custom_form_input data_search_input"
                placeholder="Search subcategory by name"
                autoComplete="off"
                autoFocus
                type={"search"}
              />
              {subCategorySearchKey === "" ? (
                <i className="bi bi-search"></i>
              ) : (
                <i
                  className="bi bi-x-lg data_search_input_clear"
                  onClick={() => setSubCategorySearchKey("")}
                ></i>
              )}
            </div>

            <button
              type="button"
              className="data_select_modal_close p-ripple"
              onClick={handleCloseSubCategoryModal}
            >
              <i className="bi bi-x"></i>
              <Ripple />
            </button>
          </div>

          <div className="data_select_modal_body has_custom_input">
            {filteredSubCategoryData && filteredSubCategoryData?.length > 0 ? (
              <div className="data_select_option_area">
                {filteredSubCategoryData?.map((subCategory) => (
                  <div
                    key={subCategory?.id}
                    className={`data_select_option p-ripple ${subCategory?.id === selectedSubCategory?.id ? "selected" : ""}
                    ${subCategory?.created_by !== null ? 'custom' : ''}`}
                    onClick={() => handleSelectSubCategory(subCategory)}
                  >
                    <span>{subCategory?.sub_category_name}</span>
                    <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />

                    {subCategory?.created_by !== null && (
                      <div className="custom_data_action_area">
                        <button className="is_btn data_select_option_edit_btn p-ripple"
                          onClick={(e) => {
                            e.stopPropagation();

                            if (selectedCustomSubCategory !== null) {
                              handleClearCustomSubCategoryFields();
                            } else {
                              handleEditCustomSubCategory(subCategory);
                            }
                          }}>
                          <i className={`bi ${(selectedCustomSubCategory !== null && selectedCustomSubCategory?.id === subCategory?.id) ? 'bi-x-lg' : 'bi-pencil-square'}`}></i>
                        </button>

                        <button className="is_btn data_select_option_edit_btn delete p-ripple"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCustomSubCategory(subCategory);
                          }}
                          disabled={deleteSubCatergoryLoadingId === subCategory?.id}
                        >
                          <i className={deleteSubCatergoryLoadingId === subCategory?.id ? 'ri-loader-4-fill loading_spinner' : 'bi bi-trash'}></i>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no_records_found">
                <i className="bi bi-exclamation-circle me-1"></i>
                No sub categories found.
              </div>
            )}
          </div>

          <div className="page_form_group data_select_modal_footer mb-0">
            <div className="input_with_button_area">
              <InputText
                id="customSubCategoryName"
                value={customSubCategoryName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomSubCategoryName(e.target.value)}
                className="custom_form_input has_add_value custom_data_input"
                placeholder="Enter subcategory name"
                autoComplete="off"
              />
              <button
                className="add_value_btn fill p-ripple"
                onClick={customDataState === 'Add' ? handleCreateCustomSubCategory : handleUpdateCustomSubCategory}
                disabled={(customCreateLoading || customSubCategoryName === "") || (customDataState === 'Edit' && !subCategoryUpdateHasChanges)}
              >
                <i className={customCreateLoading ? "ri-loader-4-fill loading_spinner" : customDataState === 'Edit' ? "bi bi-pencil-square" : "bi bi-plus-lg"}></i>
                <span>{customCreateLoading ? "Processing" : customDataState === 'Edit' ? "Update" : "Add"}</span>
                <Ripple />
              </button>
            </div>
            {customSubCategoryNameError && (
              <small className="form_error_msg">
                {customSubCategoryNameError}
              </small>
            )}
          </div>
        </div>
      </Dialog>
      {/*  */}

      {/* Share modal */}
      <ShareModal
        isOpen={showContactShareModal}
        canEditOption={true}
        loading={loading}
        sharedList={sharedList}
        projectContactIds={projectContactIds}
        onClose={handleCloseShareModal}
        onShare={shareDataFunction}
        title="Share material"
      />
      {/*  */}
    </React.Fragment>
  );
};

export default Materials;
